import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RefreshCw, Route as RouteIcon, Ruler, Layers, Waves, Mountain,
  GitFork, CircleDot, Bus, MapPin, AlertTriangle, ArrowUpDown,
} from 'lucide-react';
import { dashboardApi } from '../../services/dashboard-api.service';

// Road Mileage executive dashboard — KPIs, surface-type split, road-class split, and a
// district-wise breakdown, all from the auth-gated /dashboard/road-mileage aggregate.
const FYS = ['2025-26'];

// ---- formatters ----
const nf = new Intl.NumberFormat('en-IN');
const num = (v: any) => nf.format(Math.round(Number(v || 0)));
const km = (v: any) => `${new Intl.NumberFormat('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(v || 0))}`;

// Surface-type colours (BT / CC / WBM / Unmetalled).
const SURF_COLORS = ['#1f2937', '#2563eb', '#d97706', '#94a3b8'];

const KPI: React.FC<{ color: string; icon: React.ReactNode; label: string; value: string; sub?: string }> = ({ color, icon, label, value, sub }) => (
  <div className="rounded-lg bg-white border border-gray-200 shadow-sm px-3 py-2 flex flex-col gap-1">
    <div className="flex items-center gap-1.5 text-[11px] font-medium text-gray-500">
      <span className="inline-flex h-5 w-5 items-center justify-center rounded" style={{ background: `${color}1a`, color }}>{icon}</span>
      <span className="leading-tight">{label}</span>
    </div>
    <div className="text-lg font-bold text-gray-800 leading-none">{value}</div>
    {sub && <div className="text-[10px] text-gray-400">{sub}</div>}
  </div>
);

const Panel: React.FC<{ title: string; className?: string; children: React.ReactNode }> = ({ title, className = '', children }) => (
  <div className={`rounded-lg bg-white border border-gray-200 shadow-sm ${className}`}>
    <div className="px-3 py-2 border-b border-gray-100 text-[13px] font-semibold text-gray-700">{title}</div>
    <div className="p-3">{children}</div>
  </div>
);

type SortKey = 'district' | 'roads' | 'bt_total' | 'cc_total' | 'wbm_km' | 'unmetalled_total' | 'surface_total_km' | 'bridges' | 'culverts';

const RoadMileageDashboard: React.FC = () => {
  const [fy, setFy] = useState(FYS[0]);
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'surface_total_km', dir: -1 });

  const load = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const res = await dashboardApi.getRoadMileageDashboard({ financialYear: fy });
      setData(res);
    } catch (e: any) { setError(e?.message || 'Failed to load Road Mileage dashboard'); }
    finally { setLoading(false); }
  }, [fy]);
  useEffect(() => { load(); }, [load]);

  const k = data?.kpis || {};
  const surface: any[] = data?.surface || [];
  const classes: any[] = data?.classes || [];
  const maxClass = Math.max(1, ...classes.map((c) => Number(c.km || 0)));

  const districts = useMemo(() => {
    const arr = [...(data?.districts || [])];
    const { key, dir } = sort;
    arr.sort((a, b) => {
      const av = a[key], bv = b[key];
      if (typeof av === 'string') return String(av).localeCompare(String(bv)) * dir;
      return (Number(av || 0) - Number(bv || 0)) * dir;
    });
    return arr;
  }, [data, sort]);
  const maxLen = Math.max(1, ...(data?.districts || []).map((d: any) => Number(d.surface_total_km || 0)));

  const toggleSort = (key: SortKey) => setSort((s) => (s.key === key ? { key, dir: (s.dir === 1 ? -1 : 1) } : { key, dir: key === 'district' ? 1 : -1 }));
  const Th: React.FC<{ k: SortKey; children: React.ReactNode; className?: string }> = ({ k: key, children, className = '' }) => (
    <th onClick={() => toggleSort(key)} className={`border px-2 py-1 cursor-pointer select-none hover:bg-gray-100 whitespace-nowrap ${className}`}>
      <span className="inline-flex items-center gap-0.5">{children}<ArrowUpDown className={`h-3 w-3 ${sort.key === key ? 'text-blue-600' : 'text-gray-300'}`} /></span>
    </th>
  );

  return (
    <div className="min-h-screen bg-[#e9f1f9] text-[#1e293b]">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#15539f] to-[#2f86d4] text-white px-4 py-2.5 flex items-center justify-between">
        <div className="w-40 flex items-center gap-2 text-[11px] opacity-90">
          <select value={fy} onChange={(e) => setFy(e.target.value)} className="bg-white/15 border border-white/30 rounded px-2 py-0.5 text-white">
            {FYS.map((f) => <option key={f} className="text-gray-800" value={f}>{f}</option>)}
          </select>
        </div>
        <h1 className="text-base md:text-lg font-bold tracking-wide text-center flex-1">PRED &ndash; ROAD MILEAGE DASHBOARD</h1>
        <div className="w-40 flex items-center justify-end gap-2 text-[10px] opacity-90">
          <span>{num(k.districts)} districts &middot; FY {data?.financialYear || fy}</span>
          <button onClick={load} title="Refresh" className="p-1 rounded hover:bg-white/15"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></button>
        </div>
      </div>

      {error && <div className="m-3 bg-red-50 border border-red-200 text-red-800 text-sm rounded px-3 py-2 flex items-center gap-2"><AlertTriangle className="h-4 w-4" />{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-24 text-gray-500"><RefreshCw className="h-7 w-7 animate-spin mr-2" /> Loading Road Mileage dashboard…</div>
      ) : data && (
        <div className="p-3 space-y-3">
          {/* KPI strip */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 xl:grid-cols-10 gap-2">
            <KPI color="#2563eb" icon={<RouteIcon className="h-3.5 w-3.5" />} label="Total Roads" value={num(k.total_roads)} />
            <KPI color="#0d9488" icon={<Ruler className="h-3.5 w-3.5" />} label="Total Surfaced (km)" value={km(k.total_length_km)} />
            <KPI color="#1f2937" icon={<Layers className="h-3.5 w-3.5" />} label="Black Top (km)" value={km(k.bt_total)} />
            <KPI color="#2563eb" icon={<Layers className="h-3.5 w-3.5" />} label="Cement Concrete (km)" value={km(k.cc_total)} />
            <KPI color="#d97706" icon={<Waves className="h-3.5 w-3.5" />} label="WBM (km)" value={km(k.wbm_km)} />
            <KPI color="#94a3b8" icon={<Mountain className="h-3.5 w-3.5" />} label="Unmetalled (km)" value={km(k.unmetalled_total)} />
            <KPI color="#7c3aed" icon={<GitFork className="h-3.5 w-3.5" />} label="Bridges" value={num(k.bridges_total)} sub={`Minor ${num(k.bridges_minor)} · Major ${num(k.bridges_major)}`} />
            <KPI color="#16a34a" icon={<CircleDot className="h-3.5 w-3.5" />} label="CD / Culverts" value={num(k.culverts)} />
            <KPI color="#ea580c" icon={<Bus className="h-3.5 w-3.5" />} label="Bus-Plying (km)" value={km(k.bus_plying_km)} />
            <KPI color="#dc2626" icon={<MapPin className="h-3.5 w-3.5" />} label="Districts" value={num(k.districts)} />
          </div>

          {/* Composition row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <Panel title="Surface Composition (by length)" className="lg:col-span-2">
              {/* Segmented bar */}
              <div className="flex h-6 w-full rounded overflow-hidden mb-3">
                {surface.map((s, i) => s.pct > 0 && (
                  <div key={s.label} style={{ width: `${s.pct}%`, background: SURF_COLORS[i] }} title={`${s.label}: ${km(s.km)} km (${s.pct}%)`} />
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {surface.map((s, i) => (
                  <div key={s.label} className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-sm flex-shrink-0" style={{ background: SURF_COLORS[i] }} />
                    <div className="leading-tight">
                      <div className="text-[11px] font-medium text-gray-700">{s.label}</div>
                      <div className="text-[11px] text-gray-500">{km(s.km)} km · <span className="font-semibold">{s.pct}%</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel title="Road Classification (km)">
              <div className="space-y-2">
                {classes.map((c) => (
                  <div key={c.label}>
                    <div className="flex justify-between text-[11px] text-gray-600 mb-0.5"><span className="font-medium">{c.label}</span><span>{km(c.km)} km</span></div>
                    <div className="h-2.5 w-full bg-gray-100 rounded"><div className="h-2.5 rounded bg-[#2f86d4]" style={{ width: `${(Number(c.km || 0) / maxClass) * 100}%` }} /></div>
                  </div>
                ))}
                {classes.every((c) => !Number(c.km)) && <div className="text-[11px] text-gray-400">ODR / MDR / VR split not captured in the source sheet.</div>}
              </div>
            </Panel>
          </div>

          {/* District-wise table */}
          <Panel title={`District-wise Road Mileage — ${num(districts.length)} districts (click a header to sort)`}>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] border-collapse">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="border px-2 py-1 text-center w-8">#</th>
                    <Th k="district" className="text-left">District</Th>
                    <Th k="roads" className="text-right">Roads</Th>
                    <Th k="surface_total_km" className="text-right">Total (km)</Th>
                    <th className="border px-2 py-1 text-left w-40">Length share</th>
                    <Th k="bt_total" className="text-right">BT (km)</Th>
                    <Th k="cc_total" className="text-right">CC (km)</Th>
                    <Th k="wbm_km" className="text-right">WBM (km)</Th>
                    <Th k="unmetalled_total" className="text-right">Unmet. (km)</Th>
                    <Th k="bridges" className="text-right">Bridges</Th>
                    <Th k="culverts" className="text-right">Culverts</Th>
                  </tr>
                </thead>
                <tbody>
                  {districts.map((d: any, i: number) => (
                    <tr key={d.district_id ?? d.district ?? i} className={i % 2 ? 'bg-gray-50/60' : 'bg-white'}>
                      <td className="border px-2 py-1 text-center text-gray-400">{i + 1}</td>
                      <td className="border px-2 py-1 text-left font-medium text-gray-700">{d.district}</td>
                      <td className="border px-2 py-1 text-right tabular-nums">{num(d.roads)}</td>
                      <td className="border px-2 py-1 text-right tabular-nums font-semibold">{km(d.surface_total_km)}</td>
                      <td className="border px-2 py-1">
                        <div className="h-2 w-full bg-gray-100 rounded"><div className="h-2 rounded bg-[#15539f]" style={{ width: `${(Number(d.surface_total_km || 0) / maxLen) * 100}%` }} /></div>
                      </td>
                      <td className="border px-2 py-1 text-right tabular-nums">{km(d.bt_total)}</td>
                      <td className="border px-2 py-1 text-right tabular-nums">{km(d.cc_total)}</td>
                      <td className="border px-2 py-1 text-right tabular-nums">{km(d.wbm_km)}</td>
                      <td className="border px-2 py-1 text-right tabular-nums">{km(d.unmetalled_total)}</td>
                      <td className="border px-2 py-1 text-right tabular-nums">{num(d.bridges_minor + d.bridges_major)}</td>
                      <td className="border px-2 py-1 text-right tabular-nums">{num(d.culverts)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-blue-50 font-semibold text-gray-800">
                    <td className="border px-2 py-1" />
                    <td className="border px-2 py-1 text-left">Total (State)</td>
                    <td className="border px-2 py-1 text-right tabular-nums">{num(k.total_roads)}</td>
                    <td className="border px-2 py-1 text-right tabular-nums">{km(k.total_length_km)}</td>
                    <td className="border px-2 py-1" />
                    <td className="border px-2 py-1 text-right tabular-nums">{km(k.bt_total)}</td>
                    <td className="border px-2 py-1 text-right tabular-nums">{km(k.cc_total)}</td>
                    <td className="border px-2 py-1 text-right tabular-nums">{km(k.wbm_km)}</td>
                    <td className="border px-2 py-1 text-right tabular-nums">{km(k.unmetalled_total)}</td>
                    <td className="border px-2 py-1 text-right tabular-nums">{num(k.bridges_total)}</td>
                    <td className="border px-2 py-1 text-right tabular-nums">{num(k.culverts)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Panel>

          <p className="text-[10px] text-gray-400 px-1">
            Lengths reconcile exactly to the road-wise register; per-surface counts are the Excel-authored abstract values. Source: RM abstract (District grain).
          </p>
        </div>
      )}
    </div>
  );
};

export default RoadMileageDashboard;
